import { Box, Typography } from '@mui/material'
import { alpha, lighten, useTheme } from '@mui/material/styles'
import { keyframes } from '@mui/system'
import Image from 'next/image'
import React from 'react'
import MyProfileContext from '@/components/Context/MyProfileContext'
import { useCurrentLocale, useI18n } from '@/locales/index'
import { EntityList } from '@/types/Entity'

/**
 * Marque de chargement du SITE — utilisée avec le menu v2 (unifié, plus de changement
 * d'entité par le header, donc l'entité « sélectionnée » n'a plus de sens visuel) :
 * School pour le site pharmedischool et la Belgique, Sound pour l'Italie, sinon PharmediGroup.
 */
const getSiteBrand = (locale: string, siteBelgium: boolean): EntityList => {
  if (typeof window !== 'undefined' && window.location.hostname.includes('pharmedischool')) {
    return EntityList.PHARMEDISCHOOL
  }
  if (siteBelgium || locale === 'fr-BE' || locale === 'nl-BE') return EntityList.PHARMEDISCHOOL
  if (locale === 'it') return EntityList.PHARMEDISOUND
  return EntityList.PHARMEDIGROUP
}

// Fond « ciel » du loader (carte n° 640) : dégradé bleu très pâle, sur lequel le logo
// officiel se détache grâce à un halo blanc. Rien ne passe devant le logo.
const SKY_SURFACE = 'linear-gradient(160deg, #F7FBFF 0%, #DDEEFC 100%)'
const SKY_TEXT = '#3D5A80'
// Variante plein écran : mêmes teintes à 82 % d'opacité, avec un flou léger, pour
// qu'on devine la page en dessous sans qu'elle gêne la lecture du logo.
const SKY_VEIL =
  'linear-gradient(160deg, rgba(247, 251, 255, 0.82) 0%, rgba(221, 238, 252, 0.82) 100%)'

// Les états de repos de chaque élément sont ses états finaux : sans animation
// (prefers-reduced-motion), le loader reste lisible et immobile.

const appear = keyframes`
  from { opacity: 0; transform: translate3d(0, 16px, 0) scale(0.96); filter: blur(6px); }
  to   { opacity: 1; transform: translate3d(0, 0, 0) scale(1); filter: blur(0); }
`

// Le logo arrive de loin, flou, dépasse légèrement puis se pose.
const logoIn = keyframes`
  0%   { opacity: 0; transform: scale(1.35); filter: blur(14px); }
  60%  { opacity: 1; transform: scale(0.97); filter: blur(0); }
  100% { opacity: 1; transform: scale(1); filter: blur(0); }
`

const float = keyframes`
  0%, 100% { transform: translate3d(0, 0, 0); }
  50%      { transform: translate3d(0, -7px, 0); }
`

// Reflet lumineux qui balaie le logo, découpé à la forme exacte du logo.
const shine = keyframes`
  0%        { background-position: 160% 0; }
  60%, 100% { background-position: -60% 0; }
`

// Onde de choc à l'arrivée du logo.
const burst = keyframes`
  0%   { transform: translate(-50%, -50%) scale(0.3); opacity: 0.9; }
  100% { transform: translate(-50%, -50%) scale(2.4); opacity: 0; }
`

// Anneau d'orbite : on ne fait tourner que l'intérieur, l'inclinaison reste fixe.
const ringSpin = keyframes`
  to { transform: rotate(360deg); }
`

const ringIn = keyframes`
  from { opacity: 0; transform: scale(0.6); }
  to   { opacity: 1; transform: scale(1); }
`

const drift = keyframes`
  0%, 100% { transform: translate(-50%, -50%) translate3d(0, 0, 0) scale(1); }
  33%      { transform: translate(-50%, -50%) translate3d(30px, -18px, 0) scale(1.15); }
  66%      { transform: translate(-50%, -50%) translate3d(-26px, 14px, 0) scale(0.9); }
`

// Les particules convergent vers le logo, comme une énergie qui se rassemble.
const converge = keyframes`
  0%   { transform: translate3d(var(--x), var(--y), 0) scale(1); opacity: 0; }
  25%  { opacity: 1; }
  100% { transform: translate3d(0, 0, 0) scale(0.2); opacity: 0; }
`

const slide = keyframes`
  0%   { left: -45%; width: 45%; }
  50%  { width: 65%; }
  100% { left: 100%; width: 45%; }
`

// Voile plein écran : uniquement l'opacité, un filter sur cet élément casserait son backdrop-filter.
const veilIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`

const STAGE_WIDTH = 440
const STAGE_HEIGHT = 230
const LOGO_HEIGHT = 56
const RING_SIZE = 330

// Anneaux en perspective, entièrement derrière le logo.
const RINGS = [
  { tilt: 0, squash: 74, duration: 5, reverse: false, delay: 0.5 },
  { tilt: -16, squash: 70, duration: 7.5, reverse: true, delay: 0.7 },
]

// Positions fixes (pas d'aléatoire : rendu identique serveur et client).
const PARTICLES = Array.from({ length: 14 }, (_, index) => {
  const angle = (index / 14) * Math.PI * 2
  const distance = 150 + (index % 3) * 30
  return {
    x: Math.round(Math.cos(angle) * distance),
    y: Math.round(Math.sin(angle) * distance * 0.5),
    size: 3 + (index % 3),
    delay: ((index * 7) % 14) * 0.22,
    duration: 2.6 + (index % 4) * 0.3,
  }
})

const MainLoader = (props: any) => {
  const t = useI18n()
  const theme = useTheme()
  const locale = useCurrentLocale()
  const { siteBelgium } = React.useContext(MyProfileContext)

  // fullScreen : loader seul à l'écran (démarrage, garde d'authentification), centré
  // dans la fenêtre sur le fond ciel.
  // brand : force la marque (page de variantes), sinon celle du site.
  const { message = t('pages.loading.loadingMessage'), fullScreen = false, brand } = props

  // Menu unifie (seule version) : marque du site, pas le logo de l'entite selectionnee.
  const entityImage: EntityList = brand ?? getSiteBrand(locale, !!siteBelgium)
  const logoSrc = `/entity/single-logo/${entityImage?.toLowerCase()}.svg`
  const primary = lighten(theme.palette.primary.main, 0.2)
  const secondary = lighten(theme.palette.secondary?.main ?? theme.palette.primary.light, 0.3)
  const textColor = SKY_TEXT
  const waveColor = theme.palette.primary.main
  const letters = Array.from(String(message))

  // Chaque lettre se soulève et prend la couleur primaire à son tour : une vague parcourt le texte.
  const letterWave = React.useMemo(
    () => keyframes`
      0%, 50%, 100% { transform: translate3d(0, 0, 0); color: ${textColor}; text-shadow: none; }
      25%           { transform: translate3d(0, -5px, 0); color: ${waveColor}; text-shadow: 0 0 12px ${alpha(waveColor, 0.45)}; }
    `,
    [textColor, waveColor]
  )

  const logo = (
    <Image
      src={logoSrc}
      width={240}
      height={LOGO_HEIGHT}
      alt={'logo'}
      priority
      style={{ height: LOGO_HEIGHT, width: 'auto', maxWidth: STAGE_WIDTH - 120, display: 'block' }}
    />
  )

  const renderRing = (ring: (typeof RINGS)[number]) => (
    <Box
      key={ring.tilt}
      aria-hidden
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: RING_SIZE,
        height: RING_SIZE,
        ml: `${-RING_SIZE / 2}px`,
        mt: `${-RING_SIZE / 2}px`,
        zIndex: 1,
        transform: `rotate(${ring.tilt}deg) rotateX(${ring.squash}deg)`,
        pointerEvents: 'none',
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          animation: `${ringIn} 900ms cubic-bezier(0.22, 1, 0.36, 1) ${ring.delay}s both`,
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            border: `1px solid ${alpha(primary, 0.14)}`,
            animation: `${ringSpin} ${ring.duration}s linear infinite${ring.reverse ? ' reverse' : ''}`,
            // Traînée de comète : dégradé conique réduit à un fin anneau.
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: -1,
              borderRadius: '50%',
              background: `conic-gradient(from 0deg, transparent 0deg, transparent 200deg, ${alpha(secondary, 0.5)} 300deg, ${primary} 360deg)`,
              mask: 'radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 2px))',
              WebkitMask:
                'radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 2px))',
            },
            // Tête de la comète, au bout de la traînée.
            '&::after': {
              content: '""',
              position: 'absolute',
              top: -5,
              left: '50%',
              width: 10,
              height: 10,
              ml: '-5px',
              borderRadius: '50%',
              background: `radial-gradient(circle, #fff 0%, ${primary} 60%)`,
              boxShadow: `0 0 14px 4px ${alpha(primary, 0.7)}`,
            },
          }}
        />
      </Box>
    </Box>
  )

  const content = (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        width: '100%',
        // Hors plein écran, le loader est posé sur un panneau au fond ciel.
        ...(fullScreen
          ? {}
          : { background: SKY_SURFACE, borderRadius: 4, py: 5, overflow: 'hidden' }),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2.5,
        animation: `${appear} 600ms cubic-bezier(0.22, 1, 0.36, 1) 150ms both`,
        '@media (prefers-reduced-motion: reduce)': {
          '&, & *, & *::before, & *::after': { animation: 'none !important' },
        },
      }}
    >
      {/* Scène : grille, aurore, particules, anneaux en orbite et logo libre */}
      <Box
        sx={{
          position: 'relative',
          width: STAGE_WIDTH,
          maxWidth: '100%',
          height: STAGE_HEIGHT,
        }}
      >
        {/* Halo blanc : nappes floues qui dérivent derrière le logo et le détachent du fond */}
        {[
          {
            color: '#FFFFFF',
            top: '48%',
            left: '38%',
            size: 200,
            duration: 9,
            delay: 0,
          },
          {
            color: '#FFFFFF',
            top: '52%',
            left: '64%',
            size: 170,
            duration: 11,
            delay: -4,
          },
          {
            color: '#FFFFFF',
            top: '40%',
            left: '55%',
            size: 130,
            duration: 13,
            delay: -8,
          },
        ].map(blob => (
          <Box
            key={blob.duration}
            aria-hidden
            sx={{
              position: 'absolute',
              top: blob.top,
              left: blob.left,
              width: blob.size,
              height: blob.size,
              borderRadius: '50%',
              background: alpha(blob.color, 0.95),
              filter: 'blur(44px)',
              transform: 'translate(-50%, -50%)',
              animation: `${drift} ${blob.duration}s ease-in-out ${blob.delay}s infinite`,
            }}
          />
        ))}

        {/* Particules aspirées vers le centre */}
        {PARTICLES.map(p => (
          <Box
            key={`${p.x}-${p.y}`}
            aria-hidden
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: p.size,
              height: p.size,
              ml: `${-p.size / 2}px`,
              mt: `${-p.size / 2}px`,
              borderRadius: '50%',
              background: alpha(primary, 0.8),
              boxShadow: `0 0 ${p.size * 2}px ${alpha(primary, 0.6)}`,
              opacity: 0,
              '--x': `${p.x}px`,
              '--y': `${p.y}px`,
              animation: `${converge} ${p.duration}s cubic-bezier(0.55, 0, 0.75, 0.4) ${p.delay}s infinite`,
            }}
          />
        ))}

        {/* Onde de choc unique, à l'arrivée du logo */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: 200,
            height: 200,
            borderRadius: '50%',
            border: `2px solid ${alpha(primary, 0.5)}`,
            boxShadow: `0 0 30px ${alpha(primary, 0.35)}, inset 0 0 30px ${alpha(primary, 0.25)}`,
            opacity: 0,
            animation: `${burst} 1.1s cubic-bezier(0.22, 1, 0.36, 1) 0.75s both`,
          }}
        />

        {RINGS.map(renderRing)}

        {/* Logo libre, sans cadre : il flotte et un reflet le balaie */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: `${logoIn} 1.1s cubic-bezier(0.22, 1, 0.36, 1) 300ms both`,
          }}
        >
          <Box sx={{ position: 'relative' }}>
            <Box
              sx={{
                position: 'relative',
                animation: `${float} 4s ease-in-out 1.4s infinite`,
              }}
            >
              {logo}
              {/* Reflet lumineux découpé à la forme du logo */}
              <Box
                aria-hidden
                sx={{
                  position: 'absolute',
                  inset: 0,
                  background: `linear-gradient(105deg, transparent 35%, ${alpha('#fff', 0.95)} 50%, transparent 65%)`,
                  backgroundSize: '250% 100%',
                  backgroundPosition: '160% 0',
                  mixBlendMode: 'screen',
                  mask: `url(${logoSrc}) center / contain no-repeat`,
                  WebkitMask: `url(${logoSrc}) center / contain no-repeat`,
                  animation: `${shine} 3.4s ease-in-out 1.6s infinite`,
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Barre indéterminée : piste arrondie, segment lumineux en dégradé */}
      <Box
        aria-hidden
        sx={{
          position: 'relative',
          width: 240,
          height: 6,
          borderRadius: 3,
          overflow: 'hidden',
          bgcolor: alpha(primary, 0.12),
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: '25%',
            width: '50%',
            borderRadius: 3,
            background: `linear-gradient(90deg, ${alpha(secondary, 0)}, ${secondary} 40%, ${primary})`,
            boxShadow: `0 0 14px ${alpha(primary, 0.7)}`,
            animation: `${slide} 1.5s cubic-bezier(0.65, 0, 0.35, 1) infinite`,
          }}
        />
      </Box>

      {/* Message : une vague de couleur et de relief parcourt les lettres */}
      <Typography
        variant="body1"
        aria-label={String(message)}
        sx={{
          fontWeight: 600,
          letterSpacing: 0.4,
          color: textColor,
          textAlign: 'center',
        }}
      >
        {letters.map((letter, index) => (
          <Box
            key={index}
            component="span"
            aria-hidden
            sx={{
              display: 'inline-block',
              whiteSpace: 'pre',
              animation: `${letterWave} 2.4s ease-in-out ${index * 0.06}s infinite`,
            }}
          >
            {letter}
          </Box>
        ))}
      </Typography>
    </Box>
  )

  if (!fullScreen) return content

  // Voile ciel plein écran, légèrement transparent : il laisse deviner la page et bloque ses clics.
  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: theme.zIndex.modal + 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: SKY_VEIL,
        backdropFilter: 'blur(3px)',
        WebkitBackdropFilter: 'blur(3px)',
        animation: `${veilIn} 400ms ease-out both`,
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    >
      {content}
    </Box>
  )
}

export default MainLoader
